<template>
  <div class="funding-page">
    <header class="fp-head">
      <div>
        <h1 class="fp-title">Финансы клиентов</h1>
        <div class="fp-sub">Подтверждение депозитов и ручные начисления балансов</div>
      </div>
      <div class="fp-header-right">
        <div class="fp-stats">
          <div class="fp-stat">
            <div class="fp-stat-lbl">В очереди</div>
            <div class="fp-stat-val">{{ queueStats.pending }}</div>
          </div>
          <div class="fp-stat">
            <div class="fp-stat-lbl">Старейшая ждёт</div>
            <div class="fp-stat-val">{{ oldestWaitText || '—' }}</div>
          </div>
          <div class="fp-stat">
            <div class="fp-stat-lbl">Сумма pending USD</div>
            <div class="fp-stat-val">{{ pendingUsdText }}</div>
          </div>
        </div>
        <!-- Primary CTA — оператор чаще всего начисляет вручную
             (refund / комп / корректировки), потому кнопка большая и
             всегда на виду. -->
        <button type="button" class="fp-btn-primary fp-btn-cta" @click="activeTab = 'manual'">
          + Начислить вручную
        </button>
      </div>
    </header>

    <!-- Tabs -->
    <div class="fp-tabs">
      <button
        type="button"
        class="fp-tab"
        :class="{ active: activeTab === 'queue' }"
        @click="activeTab = 'queue'"
      >История депозитов <span class="fp-tab-cnt">{{ queue.length }}</span></button>
      <button
        type="button"
        class="fp-tab"
        :class="{ active: activeTab === 'manual' }"
        @click="activeTab = 'manual'"
      >Ручное начисление</button>
    </div>

    <!-- Queue tab: фильтры + таблица -->
    <template v-if="activeTab === 'queue'">
      <div class="fp-filters">
        <div class="fp-filter-group">
          <span class="fp-filter-label">Статус</span>
          <button
            v-for="opt in statusFilterOptions"
            :key="opt.key"
            type="button"
            class="fp-chip"
            :class="{ 'fp-chip-active': isStatusActive(opt.key) }"
            @click="toggleStatus(opt.key)"
          >{{ opt.label }}</button>
        </div>
        <div class="fp-filter-group">
          <span class="fp-filter-label">Источник</span>
          <button
            v-for="opt in [{key:'',label:'Все'},{key:'CLIENT_REQUEST',label:'Заявки клиентов'},{key:'MANUAL_CREDIT',label:'Ручные начисления'}]"
            :key="opt.key || 'all'"
            type="button"
            class="fp-chip"
            :class="{ 'fp-chip-active': filters.source === opt.key }"
            @click="setSource(opt.key)"
          >{{ opt.label }}</button>
        </div>
      </div>

    <section class="fp-card">
      <div class="fp-card-head">
        <span>{{ headerSub }}</span>
        <button type="button" class="fp-btn-ghost" @click="loadQueue" :disabled="queueLoading">
          {{ queueLoading ? 'Обновляю…' : '↻ Обновить' }}
        </button>
      </div>
      <div class="fp-table-scroll">
      <table class="fp-table">
        <thead>
          <tr>
            <th style="width:60px">#</th>
            <th>Клиент</th>
            <th>Актив</th>
            <th>Сумма</th>
            <th>Метод</th>
            <th>Reference / tx</th>
            <th>Статус</th>
            <th>Создана</th>
            <th style="width:180px"></th>
          </tr>
        </thead>
        <tbody v-if="queue.length">
          <tr v-for="deposit in queue" :key="deposit.depositId">
            <td>#{{ deposit.depositId }}</td>
            <td>{{ deposit.userLabel || ('user ' + deposit.userId) }}</td>
            <td>
              <span class="fp-asset">{{ deposit.asset }}</span>
              <span v-if="deposit.network" class="fp-net">{{ deposit.network }}</span>
            </td>
            <td class="fp-amount">
              {{ deposit.amount != null ? formatAmount(deposit.amount, deposit.asset) : '—' }}
              <span
                v-if="deposit.receivedAmount != null && deposit.amount != null && Math.abs(deposit.receivedAmount - deposit.amount) > 1e-9"
                class="fp-received"
                :title="'Ожидали ' + formatAmount(deposit.amount, deposit.asset) + ', пришло ' + formatAmount(deposit.receivedAmount, deposit.asset)"
              >факт: {{ formatAmount(deposit.receivedAmount, deposit.asset) }}</span>
            </td>
            <td><span class="fp-method">{{ deposit.paymentMethod }}</span></td>
            <td class="fp-ref">{{ deposit.bankReference || deposit.txHash || '—' }}</td>
            <td>
              <span class="fp-dep-status" :class="depStatusCss(deposit.status)">{{ depStatusLabel(deposit.status) }}</span>
              <span v-if="deposit.source === 'MANUAL_CREDIT'" class="fp-dep-source">Ручное</span>
            </td>
            <td class="fp-when">{{ formatRelative(deposit.createdAt) }}</td>
            <td class="fp-actions">
              <template v-if="deposit.status === 'PENDING'">
                <!-- Решение Trade Manager (Подтвердить/Отклонить). -->
                <button v-if="canDecide" class="fp-btn-ok" @click="openConfirm(deposit)">Подтвердить</button>
                <button v-if="canDecide" class="fp-btn-no" @click="openReject(deposit)">Отклонить</button>
                <button class="fp-btn-link" @click="openHistory(deposit)">История</button>
              </template>
              <template v-else-if="deposit.status === 'FROZEN'">
                <!-- Легаси FROZEN-депозит (заморозка перенесена на уровень
                     аккаунта в KYC-панель) — показываем read-only. -->
                <span class="fp-frozen-badge">🔒 Заморожено</span>
                <button class="fp-btn-link" @click="openHistory(deposit)">История</button>
                <span class="fp-actor" v-if="deposit.operatorEmail">{{ deposit.operatorName || deposit.operatorEmail }}</span>
              </template>
              <template v-else>
                <button class="fp-btn-link" @click="openHistory(deposit)">История</button>
                <span class="fp-actor" v-if="deposit.operatorEmail">{{ deposit.operatorName || deposit.operatorEmail }}</span>
              </template>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr><td colspan="9" class="fp-empty">По выбранным фильтрам ничего не найдено</td></tr>
        </tbody>
      </table>
      </div>
    </section>
    </template>

    <!-- Manual credit tab -->
    <section v-if="activeTab === 'manual'" class="fp-card">
      <div class="fp-card-head">Зачислить средства клиенту</div>
      <form class="fp-form" @submit.prevent="submitManualCredit">
        <div class="fp-form-row">
          <label>Клиент</label>
          <div class="fp-client-picker" v-click-outside="closePicker">
            <!-- Trigger: показывает выбранного клиента или плейсхолдер;
                 клик открывает панель со списком. -->
            <button
              type="button"
              class="fp-picker-trigger"
              :class="{ active: pickerOpen, empty: !manualForm.userId }"
              @click="togglePicker"
            >
              <span v-if="manualForm.userId" class="fp-picker-selected">
                <b>{{ manualForm.userLabel }}</b>
                <span class="fp-picker-id">id {{ manualForm.userId }}</span>
              </span>
              <span v-else class="fp-picker-placeholder">Выберите клиента</span>
              <span class="fp-picker-arrow" :class="{ open: pickerOpen }">▾</span>
            </button>

            <!-- Dropdown panel: поиск + scrollable list. Список грузится
                 при первом открытии; поиск отдельным fetch'ем с
                 server-side фильтром, поэтому за пределы первой
                 страницы (50) поиск тоже работает. -->
            <div v-if="pickerOpen" class="fp-picker-panel">
              <div class="fp-picker-search">
                <input
                  ref="pickerSearch"
                  type="text"
                  v-model="pickerQuery"
                  placeholder="Поиск: email, имя, IIN…"
                  @input="onPickerQueryInput"
                  autocomplete="off"
                />
                <span v-if="pickerLoading" class="fp-picker-spinner">…</span>
              </div>
              <ul class="fp-picker-list">
                <li
                  v-for="user in pickerClients"
                  :key="user.id"
                  :class="{ chosen: user.id === manualForm.userId }"
                  @click="pickClient(user)"
                >
                  <div class="fp-picker-row-main">
                    <b>{{ clientPrimary(user) }}</b>
                    <span class="fp-picker-row-id">id {{ user.id }}</span>
                  </div>
                  <div class="fp-picker-row-sub">
                    {{ clientSecondary(user) }}
                  </div>
                </li>
                <li v-if="!pickerClients.length && !pickerLoading" class="fp-picker-empty">
                  {{ pickerQuery ? 'По запросу никого не нашлось' : 'Список пуст' }}
                </li>
              </ul>
            </div>

            <div v-if="manualForm.userId" class="fp-picker-clear-row">
              <button type="button" class="fp-link" @click="clearPickedClient">Сбросить выбранного клиента</button>
            </div>
          </div>
        </div>

        <div class="fp-form-row fp-form-row-grid">
          <div>
            <label>Актив</label>
            <select v-model="manualForm.asset">
              <option value="USD">USD</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
              <option value="USDT">USDT</option>
              <option value="USDC">USDC</option>
              <option value="SOL">SOL</option>
            </select>
          </div>
          <div v-if="needsNetwork">
            <label>Сеть</label>
            <select v-model="manualForm.network">
              <option v-for="n in availableNetworks" :key="n.code" :value="n.code">{{ n.label }}</option>
            </select>
          </div>
          <div>
            <label>Сумма</label>
            <input type="number" step="any" v-model.number="manualForm.amount" placeholder="100.00" />
          </div>
        </div>

        <div class="fp-form-row">
          <label>Основание (обязательно)</label>
          <textarea
            v-model="manualForm.comment"
            placeholder="Например: refund по отменённому ордеру #423, либо компенсация slippage"
            rows="2"
          ></textarea>
        </div>

        <div class="fp-form-actions">
          <button type="button" class="fp-btn-ghost" @click="resetManualForm">Очистить</button>
          <button type="submit" class="fp-btn-ok" :disabled="!manualReady || manualBusy">
            {{ manualBusy ? 'Зачисляю…' : 'Зачислить' }}
          </button>
        </div>
      </form>
    </section>

    <!-- Confirm modal -->
    <div v-if="confirmFor" class="fp-modal-overlay" @click.self="closeConfirm">
      <div class="fp-modal">
        <div class="fp-modal-head">
          Подтвердить депозит #{{ confirmFor.depositId }}
          <button type="button" class="fp-modal-close" @click="closeConfirm">×</button>
        </div>
        <div class="fp-modal-body">
          <div class="fp-modal-row"><span>Клиент:</span> <b>{{ confirmFor.userLabel || ('user ' + confirmFor.userId) }}</b></div>
          <div class="fp-modal-row"><span>Ожидали:</span> <b>{{ confirmFor.amount != null ? formatAmount(confirmFor.amount, confirmFor.asset) : 'сумма не указана' }}</b></div>
          <div class="fp-modal-row"><span>Метод:</span> {{ confirmFor.paymentMethod }}</div>
          <div class="fp-modal-row" v-if="confirmFor.bankReference"><span>Reference:</span> {{ confirmFor.bankReference }}</div>
          <div class="fp-modal-row" v-if="confirmFor.txHash"><span>TX Hash:</span> <span class="fp-mono">{{ confirmFor.txHash }}</span></div>

          <label class="fp-modal-label">
            Фактически поступило ({{ confirmFor.asset }})
            <span v-if="confirmFor.amount == null" class="fp-req">— обязательно, клиент сумму не указал</span>
          </label>
          <input
            type="number"
            step="any"
            v-model.number="confirmReceivedAmount"
            :placeholder="confirmFor.amount == null ? 'введите пришедшую сумму' : 'если пусто — зачислим ожидаемое'"
          />
          <div v-if="amountMismatch" class="fp-mismatch">
            ⚠ Расхождение: ожидали {{ formatAmount(confirmFor.amount, confirmFor.asset) }},
            пришло {{ formatAmount(confirmReceivedAmount, confirmFor.asset) }}
            ({{ mismatchSign }}{{ mismatchDiff }} {{ confirmFor.asset }}). Зачислим фактическую сумму.
          </div>

          <!-- Авто KYT транзакции (только крипта). AMLBot по tx_hash:
               не-CLEAN (FLAGGED/UNKNOWN) → зачисление только после двойного
               ревью MLRO + Compliance (оба должны принять). -->
          <div v-if="isCryptoDeposit" class="fp-kyt">
            <div class="fp-kyt-head">
              <button type="button" class="fp-btn-ghost" :disabled="kytBusy" @click="runKytCheck">
                {{ kytBusy ? 'Проверяю…' : (kytResult ? 'Проверить заново' : 'Проверить транзакцию') }}
              </button>
              <span
                v-if="kytResult"
                class="fp-kyt-badge"
                :class="'fp-kyt-' + kytResult.verdict.toLowerCase()"
              >
                {{ kytVerdictLabel(kytResult.verdict) }}
                <template v-if="kytResult.riskscore != null"> · risk {{ (kytResult.riskscore * 100).toFixed(0) }}/100</template>
              </span>
            </div>
            <div v-if="kytError" class="fp-kyt-error">{{ kytError }}</div>
            <div v-if="needsReview" class="fp-kyt-note">
              Требуется двойное ревью: MLRO и Compliance должны принять, иначе зачисление невозможно.
            </div>
            <!-- На основе чего вынесен FLAGGED. -->
            <div v-if="kytResult && kytResult.flagReason" class="fp-kyt-reason fp-kyt-flagreason">
              <div class="fp-kyt-reason-title">На основе чего заблокировано:</div>
              <div class="fp-kyt-msg">{{ kytResult.flagReason }}</div>
            </div>
            <!-- Причина не-CLEAN вердикта: сообщение + сработавшие категории. -->
            <div v-if="kytResult && kytResult.verdict !== 'CLEAN'" class="fp-kyt-reason">
              <div v-if="kytResult.message" class="fp-kyt-msg">{{ kytResult.message }}</div>
              <template v-if="kytResult.signals && kytResult.signals.length">
                <div class="fp-kyt-reason-title">Сработавшие категории:</div>
                <div class="fp-kyt-sig-list">
                  <span
                    v-for="sig in kytResult.signals"
                    :key="sig.category"
                    class="fp-kyt-sig"
                    :class="{ 'fp-kyt-sig-risky': sig.risky }"
                  >{{ signalLabel(sig.category) }} · {{ (sig.weight * 100).toFixed(0) }}%</span>
                </div>
              </template>
              <div v-else-if="kytResult.verdict === 'FLAGGED'" class="fp-kyt-msg">
                Детализация категорий недоступна (тестовый режим AMLBot).
              </div>
            </div>
            <!-- Детали AMLBot для бэкофиса. -->
            <div v-if="kytResult" class="fp-kyt-details">
              <div v-if="kytResult.devMode" class="fp-kyt-dev">⚠ Тестовый режим AMLBot — данные не боевые</div>
              <div v-if="kytResult.counterpartyAddress" class="fp-kyt-drow">
                <span>Отправитель:</span> <span class="fp-mono">{{ kytResult.counterpartyAddress }}</span>
              </div>
              <template v-if="kytResult.connections && kytResult.connections.length">
                <button type="button" class="fp-kyt-toggle" @click="toggleAmlDetails">
                  <span class="fp-kyt-toggle-chev">{{ amlDetailsVisible ? '▾' : '▸' }}</span>
                  {{ amlDetailsVisible ? 'Свернуть' : 'Расширить (' + kytResult.connections.length + ')' }}
                </button>
                <div v-show="amlDetailsVisible">
                  <div
                    v-for="(c, i) in kytResult.connections"
                    :key="'conn-' + i"
                    class="fp-kyt-conn"
                    :class="{ 'fp-kyt-conn-risky': c.riskscore >= 0.5 }"
                  >
                    <span class="fp-kyt-conn-name">{{ c.name }}</span>
                    <span class="fp-kyt-conn-type">{{ connTypeLabel(c.type) }}</span>
                    <span class="fp-kyt-conn-risk">риск {{ (c.riskscore * 100).toFixed(0) }}/100</span>
                    <span class="fp-kyt-conn-meta">{{ c.hops }} переходов</span>
                  </div>
                </div>
              </template>
              <div v-if="kytResult.uid" class="fp-kyt-drow"><span>UID:</span> <span class="fp-mono">{{ kytResult.uid }}</span></div>
            </div>
            <a
              v-if="kytResult && kytResult.pdfReport && kytResult.pdfReport !== '#'"
              :href="kytResult.pdfReport" target="_blank" rel="noopener" class="fp-kyt-link"
            >Полный отчёт AMLBot ↗</a>
            <a href="https://amlbot.com/" target="_blank" rel="noopener" class="fp-kyt-link">AMLBot ↗</a>
          </div>

          <!-- Двойное ревью MLRO + Compliance при не-CLEAN KYT. -->
          <div v-if="needsReview" class="fp-kyt-review">
            <div class="fp-kyt-reason-title">Решение MLRO + Compliance</div>
            <div class="fp-kyt-rev-grid">
              <div class="fp-kyt-rev-card" :class="reviewCardClass(kytReviewState && kytReviewState.mlro)">
                <div class="fp-kyt-rev-role">MLRO</div>
                <div class="fp-kyt-rev-dec">{{ reviewDecisionLabel(kytReviewState && kytReviewState.mlro) }}</div>
              </div>
              <div class="fp-kyt-rev-card" :class="reviewCardClass(kytReviewState && kytReviewState.compliance)">
                <div class="fp-kyt-rev-role">Compliance</div>
                <div class="fp-kyt-rev-dec">{{ reviewDecisionLabel(kytReviewState && kytReviewState.compliance) }}</div>
              </div>
            </div>
            <div v-for="(rv, i) in (kytReviewState && kytReviewState.reviews) || []" :key="'rv-' + i" class="fp-kyt-rev-comment">
              <b>{{ rv.reviewerRole === 'MLRO' ? 'MLRO' : 'Compliance' }}:</b> «{{ rv.comment }}»
            </div>

            <template v-if="myReviewRoles.length">
              <div v-for="rrole in myReviewRoles" :key="rrole" class="fp-kyt-rev-action">
                <div class="fp-kyt-rev-action-head">
                  Решение как {{ roleLabel(rrole) }}
                  <span v-if="roleDecision(rrole)">— сейчас: {{ reviewDecisionLabel(roleDecision(rrole)) }} (можно пересмотреть)</span>
                </div>
                <textarea v-model="reviewComments[rrole]" rows="2" placeholder="Обоснование решения для audit"></textarea>
                <div class="fp-kyt-rev-actions">
                  <button class="fp-btn-ok" :disabled="reviewBusy || !(reviewComments[rrole] || '').trim()" @click="submitReview('ACCEPT', rrole)">✓ Принять</button>
                  <button class="fp-btn-no" :disabled="reviewBusy || !(reviewComments[rrole] || '').trim()" @click="submitReview('REJECT', rrole)">✕ Отклонить</button>
                </div>
              </div>
            </template>
            <div v-else class="fp-kyt-note">Решение принимают роли MLRO и Compliance.</div>
          </div>

          <label class="fp-modal-label">Комментарий (для аудита)</label>
          <textarea v-model="confirmComment" rows="4" class="fp-audit-comment" placeholder="Например: AMLBot risk 8/100 CLEAN; SWIFT MT103 ref ABC-123"></textarea>
        </div>
        <div class="fp-modal-foot">
          <button class="fp-btn-ghost" @click="closeConfirm">Отмена</button>
          <button
            class="fp-btn-ok"
            :disabled="confirmBusy || !canConfirm"
            @click="submitConfirm"
          >
            {{ confirmBusy ? 'Обрабатываю…' : '✓ Подтвердить и зачислить' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reject modal -->
    <div v-if="rejectFor" class="fp-modal-overlay" @click.self="closeReject">
      <div class="fp-modal">
        <div class="fp-modal-head">
          Отклонить депозит #{{ rejectFor.depositId }}
          <button type="button" class="fp-modal-close" @click="closeReject">×</button>
        </div>
        <div class="fp-modal-body">
          <div class="fp-modal-row"><span>Клиент:</span> <b>{{ rejectFor.userLabel || ('user ' + rejectFor.userId) }}</b></div>
          <div class="fp-modal-row"><span>Сумма:</span> <b>{{ rejectFor.amount != null ? formatAmount(rejectFor.amount, rejectFor.asset) : 'сумма не указана' }}</b></div>

          <label class="fp-modal-label">Основание отклонения (обязательно)</label>
          <select v-model="rejectReasonCode" class="fp-select">
            <option value="">— выберите основание —</option>
            <option value="NOT_RECEIVED">Актив / средства не поступили → статус «Пополнение не подтверждено»</option>
            <option value="CHECK_REJECTED">Отклонено по результатам проверки → статус «Пополнение отклонено» (возврат отправителю)</option>
            <option value="OTHER">Иные причины → статус «Пополнение отклонено» (возврат отправителю)</option>
          </select>
          <p class="fp-hint" v-if="rejectReasonCode === 'NOT_RECEIVED'">Зачисление не производится, возврата нет. Клиенту уйдёт уведомление «Пополнение не подтверждено».</p>
          <p class="fp-hint" v-else-if="rejectReasonCode === 'CHECK_REJECTED'">Зачисление не производится, средства подлежат возврату отправителю. Клиенту уйдёт уведомление «Пополнение отклонено».</p>
          <p class="fp-hint" v-else-if="rejectReasonCode === 'OTHER'">Зачисление не производится, средства подлежат возврату отправителю. Клиенту уйдёт уведомление «Пополнение отклонено».</p>

          <!-- Фактически поступившая сумма — фиксируем для возврата отправителю
               при отклонении по результатам проверки (может отличаться от
               заявленной клиентом). Пусто → берём ожидаемую сумму (как в
               подтверждении); обязательна только если клиент сумму не указал. -->
          <template v-if="rejectReasonCode === 'CHECK_REJECTED'">
            <label class="fp-modal-label">
              Фактически поступило ({{ rejectFor.asset }})
              <span v-if="rejectFor.amount == null" class="fp-req">— обязательно, клиент сумму не указал</span>
            </label>
            <input
              type="number"
              step="any"
              v-model.number="rejectReceivedAmount"
              :placeholder="rejectFor.amount == null ? 'введите пришедшую сумму' : 'если пусто — зафиксируем ожидаемое'"
            />
          </template>

          <label class="fp-modal-label">Комментарий для аудита (необязательно)</label>
          <textarea v-model="rejectReason" rows="2" placeholder="Служебная заметка — клиенту не показывается"></textarea>
        </div>
        <div class="fp-modal-foot">
          <button class="fp-btn-ghost" @click="closeReject">Отмена</button>
          <button class="fp-btn-no" :disabled="rejectBusy || !rejectReasonCode || rejectAmountInvalid" @click="submitReject">
            {{ rejectBusy ? 'Отклоняю…' : '✕ Отклонить' }}
          </button>
        </div>
      </div>
    </div>


    <!-- History modal: таймлайн действий над депозитом. -->
    <div v-if="historyFor" class="fp-modal-overlay" @click.self="closeHistory">
      <div class="fp-modal">
        <div class="fp-modal-head">
          История депозита #{{ historyFor.depositId }}
          <button type="button" class="fp-modal-close" @click="closeHistory">×</button>
        </div>
        <div class="fp-modal-body">
          <div v-if="historyLoading" class="fp-history-empty">Загрузка…</div>
          <div v-else-if="!historyEvents.length" class="fp-history-empty">Событий пока нет.</div>
          <ul v-else class="fp-history">
            <li v-for="ev in historyEvents" :key="ev.eventId" class="fp-history-item">
              <div class="fp-history-top">
                <span class="fp-history-type">{{ historyLabel(ev) }}</span>
                <span class="fp-history-time">{{ formatRelative(ev.occurredAt) }}</span>
              </div>
              <div v-if="ev.fromStatus || ev.toStatus" class="fp-history-flow">
                {{ ev.fromStatus || '—' }} → {{ ev.toStatus || '—' }}
              </div>
              <div v-if="ev.payload && ev.payload.verdict" class="fp-history-meta">
                Вердикт: <b>{{ ev.payload.verdict }}</b>
                <template v-if="ev.payload.riskscore != null"> · риск {{ (ev.payload.riskscore * 100).toFixed(0) }}/100</template>
              </div>
              <div v-if="ev.note" class="fp-history-note">«{{ ev.note }}»</div>
              <div v-if="ev.actorEmail" class="fp-history-actor">{{ ev.actorName || ev.actorEmail }}</div>
            </li>
          </ul>
        </div>
        <div class="fp-modal-foot">
          <button class="fp-btn-ghost" @click="closeHistory">Закрыть</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '../../config/config'

// Catalog of (asset → networks) for the manual-credit dropdown. Сети —
// чистые цепи (миграции 0072–0075). ERC-20/TRC-20/… это стандарт токена
// на цепи, не отдельная сеть.
const NETWORK_CATALOG = {
  BTC:  [{ code: 'BITCOIN',  label: 'Bitcoin' }],
  ETH:  [{ code: 'ETHEREUM', label: 'Ethereum' }],
  SOL:  [{ code: 'SOLANA',   label: 'Solana' }],
  USDT: [
    { code: 'TRON',     label: 'Tron (TRC-20)' },
    { code: 'ETHEREUM', label: 'Ethereum (ERC-20)' },
    { code: 'BSC',      label: 'BNB Smart Chain (BEP-20)' },
  ],
  USDC: [
    { code: 'ETHEREUM', label: 'Ethereum (ERC-20)' },
    { code: 'SOLANA',   label: 'Solana (SPL)' },
  ],
}

export default {
  name: 'Funding',
  directives: {
    // Локальная директива: вешает на document обработчик и закрывает
    // dropdown при клике вне элемента-носителя. Vue 2 не приносит её
    // в коробке, а тащить v-click-outside пакетом ради одной формы —
    // оверкилл.
    'click-outside': {
      bind(el, binding) {
        el.__clickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            if (typeof binding.value === 'function') binding.value(event)
          }
        }
        document.addEventListener('mousedown', el.__clickOutsideHandler__)
      },
      unbind(el) {
        document.removeEventListener('mousedown', el.__clickOutsideHandler__)
        delete el.__clickOutsideHandler__
      },
    },
  },
  data() {
    return {
      activeTab: 'queue',

      queue: [],
      queueLoading: false,

      manualForm: this.emptyManualForm(),
      manualBusy: false,

      // Client picker state — отделено от manualForm, чтобы открытие
      // и поиск не дёргали reactivity самой формы.
      pickerOpen: false,
      pickerQuery: '',
      pickerClients: [],
      pickerLoading: false,
      pickerToken: 0,
      pickerDebounce: null,

      confirmFor: null,
      confirmReceivedAmount: null,
      confirmComment: '',
      confirmBusy: false,
      kytResult: null,   // { verdict, riskscore, asset, address, status }
      kytBusy: false,
      kytError: '',
      // Двойное KYT-ревью (MLRO + Compliance) для не-CLEAN.
      kytReviewState: null,   // { reviews, mlro, compliance, bothAccepted, anyRejected }
      reviewComments: { MLRO: '', COMPILANCE: '' },
      reviewBusy: false,
      // Список контрагентов AMLBot сворачиваем (модалка длинная). null —
      // авто: CLEAN раскрыт, не-CLEAN свёрнут.
      amlDetailsOpen: null,

      rejectFor: null,
      rejectReasonCode: '',
      rejectReason: '',
      rejectReceivedAmount: null,
      rejectBusy: false,

      historyFor: null,
      historyEvents: [],
      historyLoading: false,

      // Фильтры списка. Пустой statuses → дефолт PENDING (бэк сам
      // подставит). source='' → все источники.
      filters: {
        statuses: [],
        source: '',
      },
      statusFilterOptions: [
        { key: 'PENDING',      label: 'Ожидают' },
        { key: 'FROZEN',       label: 'Заморожены' },
        { key: 'CONFIRMED',    label: 'Подтверждённые' },
        { key: 'REJECTED',     label: 'Отклонённые' },
        { key: 'NOT_RECEIVED', label: 'Не подтверждены' },
        { key: 'ALL',          label: 'Все' },
      ],
    }
  },
  computed: {
    queueStats() {
      return { pending: this.queue.filter((d) => d.status === 'PENDING').length }
    },
    // Крипто-депозит (ONCHAIN) — только для него делаем авто-KYT.
    isCryptoDeposit() {
      return !!this.confirmFor && this.confirmFor.paymentMethod === 'ONCHAIN'
    },
    // Не-CLEAN крипто-KYT требует двойного ревью (MLRO + Compliance).
    needsReview() {
      return this.isCryptoDeposit && !!this.kytResult && this.kytResult.verdict !== 'CLEAN'
    },
    // Роли, под которыми текущий пользователь может ставить решение.
    // ADMIN действует как обе роли по отдельности; выделенный ревьюер —
    // только под своей ролью.
    myReviewRoles() {
      let lu = {}
      try { lu = JSON.parse(window.localStorage.getItem('loginedUser') || '{}') } catch (e) { lu = {} }
      const roles = ((lu && lu.userRoles) || []).map(r => String((r && r.name) || '').toUpperCase())
      if (roles.indexOf('ADMIN') !== -1) return ['MLRO', 'COMPILANCE']
      const result = []
      if (roles.indexOf('MLRO') !== -1) result.push('MLRO')
      if (roles.indexOf('COMPILANCE') !== -1) result.push('COMPILANCE')
      return result
    },
    // Все роли текущего пользователя (upper-case).
    myRoles() {
      let lu = {}
      try { lu = JSON.parse(window.localStorage.getItem('loginedUser') || '{}') } catch (e) { lu = {} }
      return ((lu && lu.userRoles) || []).map(r => String((r && r.name) || '').toUpperCase())
    },
    // Trade Manager (или ADMIN) — подтверждает/отклоняет заявки.
    canDecide() {
      return this.myRoles.indexOf('ADMIN') !== -1 || this.myRoles.indexOf('TRADE_MANAGER') !== -1
    },
    reviewBothAccepted() { return !!(this.kytReviewState && this.kytReviewState.bothAccepted) },
    reviewAnyRejected() { return !!(this.kytReviewState && this.kytReviewState.anyRejected) },
    // При отклонении по результатам проверки: если клиент сумму указал —
    // поле необязательно (пусто → берём ожидаемую), но введённое значение
    // должно быть > 0. Если клиент сумму НЕ указал — ввод обязателен.
    rejectAmountInvalid() {
      if (this.rejectReasonCode !== 'CHECK_REJECTED') return false
      const v = this.rejectReceivedAmount
      const empty = v === null || v === ''
      if (this.rejectFor && this.rejectFor.amount != null) {
        return empty ? false : !(Number(v) > 0)
      }
      return empty || !(Number(v) > 0)
    },
    // Видимость списка контрагентов: явный выбор оператора, иначе авто —
    // CLEAN раскрыт, не-CLEAN свёрнут (там список обычно длинный).
    amlDetailsVisible() {
      if (this.amlDetailsOpen !== null) return this.amlDetailsOpen
      return !this.kytResult || this.kytResult.verdict === 'CLEAN'
    },
    // Доступность кнопки подтверждения (бэкенд дублирует проверки).
    canConfirm() {
      if (this.confirmBusy || this.kytBusy) return false
      if (this.receivedRequiredMissing) return false
      if (this.isCryptoDeposit) {
        if (!this.kytResult) return false
        // Не-CLEAN — зачисление только когда оба ревьюера приняли.
        if (this.needsReview && !this.reviewBothAccepted) return false
      }
      return true
    },
    // Расхождение между ожидаемой и фактической суммой в confirm-модалке.
    // Клиент не указал сумму → для зачисления оператор обязан ввести
    // фактически пришедшую (для заморозки сумма не нужна).
    receivedRequiredMissing() {
      if (!this.confirmFor) return false
      if (this.confirmFor.amount != null) return false
      return !(this.confirmReceivedAmount > 0)
    },
    amountMismatch() {
      if (!this.confirmFor || this.confirmFor.amount == null) return false
      const rec = Number(this.confirmReceivedAmount)
      if (!rec || rec <= 0) return false
      return Math.abs(rec - Number(this.confirmFor.amount || 0)) > 1e-9
    },
    mismatchDiff() {
      if (!this.confirmFor) return 0
      const diff = Number(this.confirmReceivedAmount) - Number(this.confirmFor.amount || 0)
      return Math.abs(diff).toFixed(this.confirmFor.asset === 'USD' ? 2 : 8)
    },
    mismatchSign() {
      if (!this.confirmFor) return ''
      return (Number(this.confirmReceivedAmount) - Number(this.confirmFor.amount || 0)) > 0 ? '+' : '−'
    },
    headerSub() {
      if (this.filters.statuses.length === 0) {
        return this.queue.length + ' заявок ожидают подтверждения'
      }
      if (this.filters.statuses.includes('ALL')) return 'Все депозиты (newest first)'
      return 'Фильтр: ' + this.filters.statuses.join(', ')
    },
    needsNetwork() {
      const list = NETWORK_CATALOG[this.manualForm.asset] || []
      return list.length > 0
    },
    availableNetworks() {
      return NETWORK_CATALOG[this.manualForm.asset] || []
    },
    manualReady() {
      const form = this.manualForm
      if (!form.userId) return false
      if (!form.amount || form.amount <= 0) return false
      if (!form.comment || !form.comment.trim()) return false
      if (this.needsNetwork && !form.network) return false
      return true
    },
    oldestWaitText() {
      if (!this.queue.length) return ''
      const oldest = this.queue.reduce((acc, item) => {
        const created = Date.parse(item.createdAt)
        return acc == null || created < acc ? created : acc
      }, null)
      if (!oldest) return ''
      return this.formatRelative(new Date(oldest).toISOString())
    },
    pendingUsdText() {
      const sum = this.queue
        .filter((item) => item.asset === 'USD')
        .reduce((acc, item) => acc + Number(item.amount || 0), 0)
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(sum) + ' USD'
    },
  },
  watch: {
    'manualForm.asset'(newAsset) {
      // When the operator switches asset, reseat network to the first
      // valid option for the new asset — otherwise an "ETH-TRC20" combo
      // could slip through.
      const list = NETWORK_CATALOG[newAsset] || []
      this.manualForm.network = list.length ? list[0].code : ''
    },
  },
  mounted() {
    this.loadQueue()
  },
  methods: {
    // toast — единая обёртка над bvToast с дефолтами Payton-стиля.
    toast(message, title, variant) {
      if (!this.$bvToast) return
      this.$bvToast.toast(message, {
        title: title || 'Уведомление',
        variant: variant || 'info',
        toaster: 'b-toaster-top-center',
        solid: true,
        autoHideDelay: 4000,
      })
    },
    emptyManualForm() {
      return {
        userId: 0,
        userLabel: '',
        asset: 'USD',
        network: '',
        amount: null,
        comment: '',
      }
    },
    resetManualForm() {
      this.manualForm = this.emptyManualForm()
      this.pickerQuery = ''
      this.pickerClients = []
    },
    clearPickedClient() {
      this.manualForm.userId = 0
      this.manualForm.userLabel = ''
    },

    // ---- Queue ----------------------------------------------------
    loadQueue() {
      this.queueLoading = true
      const params = {}
      if (this.filters.statuses.length) params.status = this.filters.statuses.join(',')
      if (this.filters.source) params.source = this.filters.source
      axios.get(`${apiDomain}/admin/funding/queue`, { params, headers: getHeader() })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : []
          this.queue = list
          list.forEach((item) => this.hydrateUserLabel(item))
        })
        .catch((err) => {
          console.error('queue load failed:', err)
        })
        .finally(() => { this.queueLoading = false })
    },
    isStatusActive(key) {
      if (key === 'ALL') return this.filters.statuses.length === 1 && this.filters.statuses[0] === 'ALL'
      // PENDING — это дефолт «без фильтра», подсвечиваем чип PENDING
      // и когда пользователь явно ничего не выбрал, чтобы это
      // визуально соответствовало текущему отображению.
      if (key === 'PENDING' && this.filters.statuses.length === 0) return true
      return this.filters.statuses.includes(key)
    },
    toggleStatus(key) {
      // Single-select: каждый клик заменяет выбор, не добавляет.
      // Клик по уже активному чипу игнорируем, чтобы не плодить
      // лишние API-запросы при двойном клике.
      if (this.isStatusActive(key)) return
      if (key === 'ALL') {
        this.filters.statuses = ['ALL']
      } else if (key === 'PENDING') {
        // PENDING = дефолт «без фильтра» (бэкенд сам подставит).
        // Пустой массив проще читать в filters → params.
        this.filters.statuses = []
      } else {
        this.filters.statuses = [key]
      }
      this.loadQueue()
    },
    setSource(value) {
      this.filters.source = value
      this.loadQueue()
    },
    depStatusLabel(status) {
      return ({
        PENDING:      'Ожидает',
        CONFIRMED:    'Подтверждён',
        REJECTED:     'Отклонён',
        NOT_RECEIVED: 'Не подтверждён',
        EXPIRED:      'Истёк',
        FROZEN:       'Заморожен',
      })[status] || status
    },
    depStatusCss(status) {
      return ({
        PENDING:      'fp-dep-status-pending',
        CONFIRMED:    'fp-dep-status-ok',
        REJECTED:     'fp-dep-status-no',
        NOT_RECEIVED: 'fp-dep-status-no',
        EXPIRED:      'fp-dep-status-muted',
        FROZEN:       'fp-dep-status-frozen',
      })[status] || ''
    },
    hydrateUserLabel(item) {
      if (!item.userId) return
      axios.get(`${apiDomain}/exchange/userdetails/${item.userId}`, { headers: getHeader() })
        .then((res) => {
          const user = res.data || {}
          this.$set(item, 'userLabel', user.email || user.username || ('user ' + item.userId))
        })
        .catch(() => { /* leave userLabel undefined, UI falls back to 'user N' */ })
    },

    // ---- Confirm modal --------------------------------------------
    openConfirm(deposit) {
      this.confirmFor = deposit
      this.confirmReceivedAmount = null
      this.confirmComment = ''
      this.kytResult = null
      this.kytError = ''
      this.kytReviewState = null
      this.reviewComments = { MLRO: '', COMPILANCE: '' }
      this.amlDetailsOpen = null
      // Подтягиваем последнюю KYT-проверку (если была) — чтобы сразу
      // показать вердикт/причину без повторного запроса к AMLBot.
      if (deposit.paymentMethod === 'ONCHAIN') {
        axios.get(`${apiDomain}/admin/funding/${deposit.depositId}/kyt`, { headers: getHeader() })
          .then((res) => {
            if (res.data && res.data.verdict && this.confirmFor && this.confirmFor.depositId === deposit.depositId) {
              this.kytResult = res.data
              if (res.data.verdict !== 'CLEAN') this.loadKytReview()
            }
          })
          .catch(() => { /* нет проверки — оставляем kytResult=null */ })
      }
    },
    closeConfirm() {
      this.confirmFor = null
      this.confirmReceivedAmount = null
      this.confirmComment = ''
      this.kytResult = null
      this.kytError = ''
      this.kytReviewState = null
      this.reviewComments = { MLRO: '', COMPILANCE: '' }
      this.amlDetailsOpen = null
    },
    // Загрузка решений двойного ревью по текущему депозиту.
    loadKytReview() {
      if (!this.confirmFor) return
      axios.get(`${apiDomain}/admin/funding/${this.confirmFor.depositId}/kyt-review`, { headers: getHeader() })
        .then((res) => { this.kytReviewState = res.data || null })
        .catch(() => { /* нет прав/данных — блок ревью просто не покажется */ })
    },
    // Ревьюер ставит решение под ролью role с обязательным комментарием.
    // Для ADMIN role выбирается из UI, для выделенного ревьюера бэкенд
    // всё равно перезапишет её на собственную.
    submitReview(decision, role) {
      const comment = (this.reviewComments[role] || '').trim()
      if (!this.confirmFor || !comment) return
      this.reviewBusy = true
      axios.post(
        `${apiDomain}/admin/funding/${this.confirmFor.depositId}/kyt-review`,
        { decision: decision, comment: comment, role: role },
        { headers: getHeader() },
      )
        .then((res) => {
          this.reviewComments[role] = ''
          // REJECT хотя бы одного ревьюера → депозит автоматически отклонён.
          if (res && res.data && res.data.rejected) {
            this.toast('Пополнение отклонено по KYT-ревью.', 'Отклонено', 'warning')
            this.closeConfirm()
            this.loadQueue()
            return
          }
          // Оба приняли → депозит автоматически зачислен.
          if (res && res.data && res.data.confirmed) {
            this.toast('Пополнение зачислено — оба ревьюера приняли.', 'Зачислено', 'success')
            this.closeConfirm()
            this.loadQueue()
            return
          }
          this.loadKytReview()
          this.toast('Решение сохранено.', 'KYT-ревью', 'success')
        })
        .catch((err) => {
          const body = err && err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || 'Не удалось сохранить решение.', 'Ошибка', 'danger')
        })
        .finally(() => { this.reviewBusy = false })
    },
    toggleAmlDetails() { this.amlDetailsOpen = !this.amlDetailsVisible },
    roleLabel(role) {
      return role === 'MLRO' ? 'MLRO' : 'Compliance'
    },
    roleDecision(role) {
      if (!this.kytReviewState) return null
      return role === 'MLRO' ? this.kytReviewState.mlro : this.kytReviewState.compliance
    },
    reviewDecisionLabel(d) {
      if (d === 'ACCEPT') return '✓ Принял'
      if (d === 'REJECT') return '✕ Отклонил'
      return 'Ожидает решения'
    },
    reviewCardClass(d) {
      if (d === 'ACCEPT') return 'fp-rev-accept'
      if (d === 'REJECT') return 'fp-rev-reject'
      return 'fp-rev-pending'
    },
    kytVerdictLabel(verdict) {
      return ({
        CLEAN: 'CLEAN — чисто',
        FLAGGED: 'FLAGGED — риск',
        UNKNOWN: 'UNKNOWN — не определено',
      })[verdict] || verdict
    },
    // Человекочитаемое название категории риска AMLBot.
    // Тип сущности-контрагента AMLBot → человекочитаемо.
    connTypeLabel(t) {
      const key = String(t || '').toLowerCase()
      return ({
        exchange_licensed: 'Лицензир. биржа',
        exchange_unlicensed: 'Нелицензир. биржа',
        exchange: 'Биржа',
        exchange_fraudulent: 'Мошенническая биржа',
        risky_exchange: 'Рискованная биржа',
        p2p_exchange: 'P2P-биржа',
        p2p_exchange_licensed: 'P2P (лиценз.)',
        p2p_exchange_unlicensed: 'P2P (нелиценз.)',
        mixer: 'Миксер',
        sanctions: 'Санкции',
        terrorism_financing: 'Финанс. терроризма',
        dark_market: 'Даркнет-маркет',
        dark_service: 'Даркнет-сервис',
        scam: 'Скам',
        ransom: 'Вымогательство',
        stolen_coins: 'Краденые средства',
        seized_assets: 'Изъятые активы',
        illegal_service: 'Нелегальный сервис',
        enforcement_action: 'Правоохран. меры',
        malware: 'Вредоносное ПО',
        gambling: 'Гэмблинг',
        payment: 'Платёжный сервис',
        wallet: 'Кошелёк',
        atm: 'Крипто-ATM',
        miner: 'Майнинг',
        marketplace: 'Маркетплейс',
        merchant_services: 'Мерчант-сервис',
      })[key] || t
    },
    signalLabel(category) {
      return ({
        sanctions: 'Санкции',
        terrorism_financing: 'Финансирование терроризма',
        stolen_coins: 'Краденые средства',
        dark_market: 'Даркнет-маркет',
        dark_service: 'Даркнет-сервис',
        scam: 'Скам / мошенничество',
        ransom: 'Вымогательство',
        mixer: 'Миксер',
        child_exploitation: 'Эксплуатация детей',
        illegal_service: 'Нелегальный сервис',
        exchange_fraudulent: 'Мошенническая биржа',
        enforcement_action: 'Правоохранительные меры',
        seized_assets: 'Изъятые активы',
        malware: 'Вредоносное ПО',
        risky_exchange: 'Рискованная биржа',
        p2p_exchange_mlrisk_high: 'P2P — высокий риск',
        exchange: 'Биржа',
        p2p_exchange: 'P2P-биржа',
        gambling: 'Гэмблинг',
        atm: 'Крипто-ATM',
        miner: 'Майнинг',
        marketplace: 'Маркетплейс',
        payment: 'Платёжный сервис',
        wallet: 'Кошелёк',
        merchant_services: 'Мерчант-сервис',
        liquidity_pools: 'Пулы ликвидности',
        unnamed_service: 'Неназванный сервис',
        infrastructure_as_a_service: 'Инфраструктура (IaaS)',
        decentralized_exchange_contract: 'DEX-контракт',
        other: 'Прочее',
      })[category] || category
    },
    // Авто-проверка транзакции депозита через AMLBot (по tx_hash).
    // Результат сохраняется в p_deposit; вердикт определяет действие.
    runKytCheck() {
      const deposit = this.confirmFor
      if (!deposit) return
      this.kytBusy = true
      this.kytError = ''
      axios.post(
        `${apiDomain}/admin/funding/${deposit.depositId}/kyt-check`,
        {},
        { headers: getHeader() },
      )
        .then((res) => {
          this.kytResult = res.data || null
          // Повторная проверка на бэке сбрасывает прежние решения ревью.
          this.kytReviewState = null
          this.amlDetailsOpen = null
          if (this.kytResult && this.kytResult.verdict !== 'CLEAN') {
            this.loadKytReview()
            this.toast(
              `Транзакция: ${this.kytVerdictLabel(this.kytResult.verdict)}.`,
              'KYT проверка', 'warning')
          }
        })
        .catch((err) => {
          const body = err && err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.kytError = body || 'Не удалось проверить транзакцию. Попробуйте ещё раз.'
        })
        .finally(() => { this.kytBusy = false })
    },
    submitConfirm() {
      if (!this.confirmFor || !this.canConfirm) return
      const payload = {
        receivedAmount: this.confirmReceivedAmount && this.confirmReceivedAmount > 0 ? this.confirmReceivedAmount : 0,
        comment: this.confirmComment.trim(),
      }
      this.confirmBusy = true
      axios.post(
        `${apiDomain}/admin/funding/${this.confirmFor.depositId}/confirm`,
        payload,
        { headers: getHeader() },
      )
        .then(() => {
          this.toast('Депозит подтверждён, баланс зачислен.', 'Готово', 'success')
          this.closeConfirm()
          this.loadQueue()
        })
        .catch((err) => {
          console.error('confirm failed:', err)
          const body = err && err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = body || 'Сервер вернул ошибку'
          if (body.indexOf('kyt-required') !== -1) {
            msg = 'Сначала проверьте транзакцию (KYT).'
          } else if (body.indexOf('kyt-review-pending') !== -1) {
            msg = 'Ожидаются решения MLRO и Compliance по KYT.'
          } else if (body.indexOf('kyt-review-rejected') !== -1) {
            msg = 'KYT-ревью отклонено одним из ревьюеров — зачисление невозможно.'
          }
          this.toast(msg, 'Не удалось обработать', 'danger')
        })
        .finally(() => { this.confirmBusy = false })
    },

    // ---- Reject modal ---------------------------------------------
    openReject(deposit) {
      this.rejectFor = deposit
      this.rejectReasonCode = ''
      this.rejectReason = ''
      this.rejectReceivedAmount = null
    },
    closeReject() {
      this.rejectFor = null
      this.rejectReasonCode = ''
      this.rejectReason = ''
      this.rejectReceivedAmount = null
    },
    submitReject() {
      if (!this.rejectFor || !this.rejectReasonCode || this.rejectAmountInvalid) return
      this.rejectBusy = true
      const payload = { reason_code: this.rejectReasonCode, reason: this.rejectReason.trim() }
      // Фактическую сумму передаём только при отклонении по проверке —
      // фиксируется для возврата отправителю. Пусто → берём ожидаемую
      // сумму заявки (как в подтверждении).
      if (this.rejectReasonCode === 'CHECK_REJECTED') {
        let amt = Number(this.rejectReceivedAmount)
        if (!(amt > 0) && this.rejectFor.amount != null) {
          amt = Number(this.rejectFor.amount)
        }
        if (amt > 0) payload.received_amount = amt
      }
      axios.post(
        `${apiDomain}/admin/funding/${this.rejectFor.depositId}/reject`,
        payload,
        { headers: getHeader() },
      )
        .then(() => {
          const msg = this.rejectReasonCode === 'NOT_RECEIVED'
            ? 'Статус: «Пополнение не подтверждено». Клиент уведомлён.'
            : 'Статус: «Пополнение отклонено». Клиент уведомлён.'
          this.toast(msg, 'Готово', 'success')
          this.closeReject()
          this.loadQueue()
        })
        .catch((err) => {
          console.error('reject failed:', err)
          const body = err && err.response && typeof err.response.data === 'string' ? err.response.data : ''
          this.toast(body || 'Сервер вернул ошибку', 'Не удалось отклонить', 'danger')
        })
        .finally(() => { this.rejectBusy = false })
    },

    // ---- History modal --------------------------------------------
    openHistory(deposit) {
      this.historyFor = deposit
      this.historyEvents = []
      this.historyLoading = true
      axios.get(`${apiDomain}/admin/funding/${deposit.depositId}/events`, { headers: getHeader() })
        .then((res) => { this.historyEvents = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.historyEvents = [] })
        .finally(() => { this.historyLoading = false })
    },
    closeHistory() {
      this.historyFor = null
      this.historyEvents = []
    },
    historyLabel(ev) {
      // Решение ревьюера (MLRO/Compliance): заголовок несёт роль и вердикт,
      // чтобы в истории было видно и согласование, и его отзыв.
      if (ev.eventType === 'KYT_CHECK' && ev.payload && ev.payload.decision) {
        const role = ev.payload.role === 'COMPILANCE' ? 'Compliance' : (ev.payload.role || 'KYT')
        const act = ev.payload.decision === 'ACCEPT' ? 'согласовал' : 'отклонил'
        return `${role}: ${act}`
      }
      return ({
        CREATED:       'Заявка создана',
        KYT_CHECK:     'KYT-проверка',
        FROZEN:        'Заморожен',
        UNFROZEN:      'Разморожен и зачислен',
        CONFIRMED:     'Подтверждён и зачислен',
        REJECTED:      'Отклонён',
        MANUAL_CREDIT: 'Ручное зачисление',
        HASH_REMINDER: 'Напоминание клиенту (TX Hash)',
      })[ev.eventType] || ev.eventType
    },

    // ---- Client picker --------------------------------------------
    togglePicker() {
      if (this.pickerOpen) {
        this.closePicker()
      } else {
        this.openPicker()
      }
    },
    openPicker() {
      this.pickerOpen = true
      // Первая загрузка — без фильтра. Дальше пользователь вводит
      // запрос → onPickerQueryInput тригерит новый fetch.
      if (!this.pickerClients.length) {
        this.fetchClients('')
      }
      // Auto-focus search input on next tick (когда DOM появился).
      this.$nextTick(() => {
        const input = this.$refs.pickerSearch
        if (input && input.focus) input.focus()
      })
    },
    closePicker() {
      this.pickerOpen = false
    },
    onPickerQueryInput() {
      // Debounce ~220 мс, чтоб не дёргать /user/list на каждую букву.
      if (this.pickerDebounce) clearTimeout(this.pickerDebounce)
      this.pickerDebounce = setTimeout(() => {
        this.fetchClients(this.pickerQuery.trim())
      }, 220)
    },
    fetchClients(query) {
      this.pickerLoading = true
      const token = ++this.pickerToken
      const body = {
        filter: query || '',
        audience: 'clients',
        currentPage: 1,
        perPage: 50,
      }
      axios.post(`${apiDomain}/user/list`, body, { headers: getHeader() })
        .then((res) => {
          if (token !== this.pickerToken) return
          const payload = res.data || {}
          this.pickerClients = Array.isArray(payload.items) ? payload.items : []
        })
        .catch(() => {
          if (token !== this.pickerToken) return
          this.pickerClients = []
        })
        .finally(() => {
          if (token === this.pickerToken) this.pickerLoading = false
        })
    },
    pickClient(user) {
      this.manualForm.userId = user.id
      this.manualForm.userLabel = this.clientPrimary(user)
      this.closePicker()
    },
    clientPrimary(user) {
      // Что показывать как «главную» строку: email если есть, иначе
      // username (в Payton это обычно одно и то же), иначе ФИО.
      if (user.email) return user.email
      if (user.username) return user.username
      const parts = [user.lastName, user.firstName].filter(Boolean)
      if (parts.length) return parts.join(' ')
      return 'user ' + user.id
    },
    clientSecondary(user) {
      // Вторая строка — IIN и статус, чтоб оператор быстро отличал
      // двух «Айбеков» друг от друга.
      const bits = []
      if (user.iin) bits.push('ИИН ' + user.iin)
      if (user.clientCategory && user.clientCategory !== 'NONE') {
        bits.push(user.clientCategory)
      }
      return bits.length ? bits.join(' · ') : '—'
    },
    submitManualCredit() {
      if (!this.manualReady) return
      const payload = {
        userId: this.manualForm.userId,
        asset: this.manualForm.asset,
        network: this.needsNetwork ? this.manualForm.network : '',
        amount: this.manualForm.amount,
        comment: this.manualForm.comment.trim(),
      }
      this.manualBusy = true
      axios.post(`${apiDomain}/admin/funding/manual-credit`, payload, { headers: getHeader() })
        .then((res) => {
          this.toast(
            'Deposit #' + (res.data && res.data.depositId || '?') + ' создан, баланс клиента обновлён.',
            'Зачисление выполнено', 'success',
          )
          this.resetManualForm()
        })
        .catch((err) => {
          console.error('manual credit failed:', err)
          const msg = (err && err.response && err.response.data) || ''
          this.toast(typeof msg === 'string' && msg ? msg : 'Сервер вернул ошибку',
            'Не удалось зачислить', 'danger')
        })
        .finally(() => { this.manualBusy = false })
    },

    // ---- Formatters -----------------------------------------------
    formatAmount(value, asset) {
      const formatted = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: asset === 'USD' ? 2 : 8,
      }).format(value || 0)
      return formatted + ' ' + asset
    },
    formatRelative(iso) {
      if (!iso) return ''
      const diff = Date.now() - Date.parse(iso)
      if (!Number.isFinite(diff)) return ''
      const minutes = Math.round(diff / 60000)
      if (minutes < 1) return 'только что'
      if (minutes < 60) return minutes + ' мин назад'
      const hours = Math.round(minutes / 60)
      if (hours < 24) return hours + ' ч назад'
      const days = Math.round(hours / 24)
      return days + ' дн назад'
    },
  },
}
</script>

<style scoped>
.funding-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  color: #111;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
}

.fp-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}
.fp-title { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.fp-sub { font-size: 12px; color: #6B7280; margin-top: 4px; }
.fp-stats { display: flex; gap: 10px; }
.fp-stat {
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 10px;
  padding: 10px 14px;
  min-width: 130px;
}
.fp-stat-lbl { font-size: 9.5px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: .06em; }
.fp-stat-val { font-size: 16px; font-weight: 800; color: #0A0A0A; margin-top: 4px; }

.fp-header-right { display: flex; align-items: center; gap: 16px; }
.fp-btn-primary {
  background: #0D7A5F; color: #fff; border: 1px solid #0D7A5F;
  border-radius: 8px; padding: 9px 16px;
  font: 700 12px/1 Inter; cursor: pointer;
  transition: all .12s;
}
.fp-btn-primary:hover { background: #0B6A52; }
.fp-btn-cta { font-size: 13px; padding: 11px 18px; }

.fp-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 10px;
}
.fp-filter-group { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.fp-filter-label {
  font: 700 10px/1 Inter;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #9CA3AF;
  margin-right: 4px;
}
.fp-chip {
  border: 1px solid #EAECF0; background: #fff;
  padding: 6px 12px; border-radius: 999px;
  font: 600 11px/1 Inter; color: #374151; cursor: pointer;
  transition: all .12s;
}
.fp-chip:hover { background: #F9FAFB; }
.fp-chip-active { background: #0D7A5F; border-color: #0D7A5F; color: #fff; }
.fp-chip-active:hover { background: #0B6A52; }

.fp-dep-status {
  display: inline-block;
  font: 700 10px/1 Inter;
  padding: 3px 8px;
  border-radius: 5px;
  margin-right: 4px;
}
.fp-dep-status-pending { background: #FFFBEB; color: #92400E; }
.fp-dep-status-ok      { background: #ECFDF5; color: #065F46; }
.fp-dep-status-no      { background: #FEF2F2; color: #991B1B; }
.fp-dep-status-muted   { background: #F3F4F6; color: #6B7280; }
.fp-dep-status-frozen  { background: #EFF6FF; color: #1E40AF; }
.fp-received {
  display: block; margin-top: 2px;
  font: 700 10.5px/1.2 Inter; color: #B45309;
}

.fp-mono { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; word-break: break-all; }
.fp-mismatch {
  margin-top: 6px; padding: 8px 11px;
  background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px;
  font-size: 11.5px; line-height: 1.5; color: #92400E;
}
.fp-kyt {
  margin-top: 14px; padding: 11px 13px;
  background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px;
}
.fp-kyt-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fp-kyt-badge {
  font-size: 12px; font-weight: 700;
  padding: 3px 10px; border-radius: 999px; border: 1px solid transparent;
}
.fp-kyt-clean   { background: #ECFDF5; color: #047857; border-color: #A7F3D0; }
.fp-kyt-flagged { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; }
.fp-kyt-unknown { background: #FFFBEB; color: #92400E; border-color: #FDE68A; }
.fp-kyt-error { margin-top: 8px; font-size: 12px; color: #B91C1C; }
.fp-kyt-note { margin-top: 8px; font-size: 12px; color: #6B7280; }
.fp-kyt-link { display: inline-block; margin-top: 8px; font-size: 11.5px; font-weight: 700; color: #0D7A5F; }
.fp-kyt-link:hover { text-decoration: underline; }
.fp-kyt-reason {
  margin-top: 8px; padding: 8px 10px;
  background: #FEF2F2; border: 1px solid #FECACA; border-radius: 7px;
}
.fp-kyt-msg { font-size: 12px; color: #92400E; }
.fp-kyt-reason-title { margin-top: 6px; font-size: 11.5px; font-weight: 700; color: #6B7280; }
.fp-kyt-sig-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.fp-kyt-sig {
  font-size: 11.5px; padding: 2px 8px; border-radius: 999px;
  background: #F3F4F6; color: #374151; border: 1px solid #E5E7EB;
}
.fp-kyt-sig-risky { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; font-weight: 700; }
.fp-kyt-flagreason { background: #FEF2F2; border-color: #FECACA; }
.fp-kyt-flagreason .fp-kyt-msg { color: #B91C1C; }
.fp-kyt-details { margin-top: 8px; padding: 8px 10px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 7px; }
.fp-kyt-dev { font-size: 11.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 6px; padding: 4px 8px; margin-bottom: 6px; }
.fp-kyt-drow { font-size: 12px; color: #374151; margin: 3px 0; }
.fp-kyt-drow > span:first-child { color: #6B7280; }
.fp-kyt-conn { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; font-size: 11.5px; padding: 4px 0; border-bottom: 1px solid #F0F2F5; }
.fp-kyt-conn:last-child { border-bottom: none; }
.fp-kyt-conn-name { font-weight: 700; color: #111827; }
.fp-kyt-conn-type { color: #6B7280; }
.fp-kyt-conn-risk { color: #374151; }
.fp-kyt-conn-meta { color: #9CA3AF; margin-left: auto; }
.fp-kyt-conn-risky .fp-kyt-conn-name,
.fp-kyt-conn-risky .fp-kyt-conn-risk { color: #B91C1C; }
.fp-kyt-toggle {
  display: inline-flex; align-items: center; gap: 6px; margin-top: 6px; padding: 4px 8px;
  background: #F3F4F6; border: 1px solid #E5E7EB; border-radius: 6px; cursor: pointer;
  font-size: 11.5px; font-weight: 700; color: #6B7280;
}
.fp-kyt-toggle:hover { background: #E5E7EB; }
.fp-kyt-toggle-chev { font-size: 10px; }
.fp-kyt-review { margin-top: 10px; padding: 10px; border: 1px solid #E5E7EB; border-radius: 8px; background: #FBFBFC; }
.fp-kyt-rev-grid { display: flex; gap: 8px; margin-top: 6px; }
.fp-kyt-rev-card { flex: 1; padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 7px; background: #fff; }
.fp-kyt-rev-role { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; }
.fp-kyt-rev-dec { font-size: 13px; font-weight: 700; margin-top: 2px; color: #374151; }
.fp-rev-accept { border-color: #A7F3D0; background: #ECFDF5; }
.fp-rev-accept .fp-kyt-rev-dec { color: #047857; }
.fp-rev-reject { border-color: #FECACA; background: #FEF2F2; }
.fp-rev-reject .fp-kyt-rev-dec { color: #B91C1C; }
.fp-rev-pending .fp-kyt-rev-dec { color: #9CA3AF; }
.fp-kyt-rev-comment { font-size: 12px; color: #4B5563; margin-top: 6px; font-style: italic; }
.fp-kyt-rev-actions { display: flex; gap: 8px; margin-top: 6px; }
.fp-kyt-rev-actions button { padding: 6px 14px; border-radius: 7px; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.fp-kyt-rev-action { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #E5E7EB; }
.fp-kyt-rev-action-head { font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.fp-kyt-rev-action-head span { font-weight: 400; color: #6B7280; }
.fp-dep-source {
  font: 700 10px/1 Inter;
  padding: 3px 8px;
  border-radius: 5px;
  background: #EFF6FF; color: #1D4ED8;
}
.fp-actor { font: 600 10.5px/1 Inter; color: #6B7280; }

.fp-tabs { display: flex; gap: 6px; margin-bottom: 14px; }
.fp-tab {
  background: transparent;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 8px 14px;
  font: 600 12px/1 Inter;
  color: #374151;
  cursor: pointer;
}
.fp-tab.active { background: #0D7A5F; border-color: #0D7A5F; color: #fff; }
.fp-tab-cnt {
  background: rgba(255,255,255,0.2);
  border-radius: 999px;
  padding: 1px 7px;
  font-weight: 700;
  margin-left: 6px;
}
.fp-tab:not(.active) .fp-tab-cnt { background: #F3F4F6; color: #0D7A5F; }

.fp-card {
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  overflow: hidden;
}
.fp-card-head {
  padding: 12px 18px;
  background: #FAFAFA;
  border-bottom: 1px solid #F0F2F5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 700;
  color: #0A0A0A;
}

.fp-table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.fp-table { width: 100%; min-width: 760px; border-collapse: collapse; }
.fp-table thead th {
  padding: 8px 14px;
  text-align: left;
  font-size: 9.5px;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: .06em;
  border-bottom: 1px solid #F0F2F5;
  background: #FAFAFA;
}
.fp-table tbody td { padding: 11px 14px; border-bottom: 1px solid #F5F6F8; font-size: 12.5px; color: #374151; }
.fp-table tbody tr:last-child td { border-bottom: none; }
.fp-amount { font-weight: 700; color: #0A0A0A; }
.fp-asset { font-weight: 700; }
.fp-net { font-size: 10px; color: #6B7280; margin-left: 4px; padding: 1px 6px; border-radius: 4px; background: #F3F4F6; }
.fp-method { font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 5px; background: #EFF6FF; color: #1D4ED8; }
.fp-ref { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; color: #6B7280; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fp-when { color: #6B7280; font-size: 11px; }
.fp-actions { display: flex; gap: 6px; }
.fp-empty { text-align: center; padding: 32px 14px; color: #9CA3AF; font-style: italic; }

.fp-btn-ok,
.fp-btn-no,
.fp-btn-ghost {
  border: 1px solid #EAECF0;
  background: #fff;
  padding: 6px 10px;
  border-radius: 7px;
  font: 600 11px/1 Inter;
  cursor: pointer;
  transition: all .12s;
}
.fp-btn-ok { background: #ECFDF5; border-color: #6EE7B7; color: #065F46; }
.fp-btn-ok:hover { background: #D1FAE5; }
.fp-btn-no { background: #FEF2F2; border-color: #FECACA; color: #991B1B; }
.fp-btn-no:hover { background: #FEE2E2; }
.fp-btn-ghost { color: #374151; }
.fp-btn-ghost:hover { background: #F9FAFB; }
.fp-btn-warn {
  border: 1px solid #BFDBFE;
  background: #EFF6FF;
  color: #1E40AF;
  padding: 6px 10px;
  border-radius: 7px;
  font: 600 11px/1 Inter;
  cursor: pointer;
  transition: all .12s;
}
.fp-btn-warn:hover { background: #DBEAFE; }
.fp-btn-ok:disabled,
.fp-btn-no:disabled,
.fp-btn-warn:disabled { opacity: 0.55; cursor: not-allowed; }

/* Бейдж «заморожено» рядом с действиями FROZEN-депозита. */
.fp-frozen-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  background: #EFF6FF;
  color: #1E40AF;
  font: 700 11px/1 Inter;
  border: 1px solid #BFDBFE;
}

/* Выпадающий список причины отклонения. */
.fp-select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font: 400 13px/1.3 Inter;
  color: #111827;
  background: #fff;
}
/* Пояснение под полем (что получит клиент). */
.fp-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #6B7280;
}

.fp-form { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
.fp-form-row label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: 6px;
}
.fp-form-row input,
.fp-form-row select,
.fp-form-row textarea {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font: 500 13px/1.4 Inter;
  color: #0A0A0A;
  background: #fff;
  outline: none;
}
.fp-form-row textarea { resize: vertical; font-family: inherit; }
.fp-form-row input:focus,
.fp-form-row select:focus,
.fp-form-row textarea:focus { border-color: #16A37F; box-shadow: 0 0 0 3px rgba(22,163,127,0.12); }
.fp-form-row-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.fp-form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 6px;
  border-top: 1px solid #F0F2F5;
}

.fp-client-picker { position: relative; }
.fp-picker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 11px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color .12s, box-shadow .12s;
}
.fp-picker-trigger.empty .fp-picker-placeholder { color: #9CA3AF; }
.fp-picker-trigger:hover { border-color: #C7D2D9; }
.fp-picker-trigger.active { border-color: #16A37F; box-shadow: 0 0 0 3px rgba(22,163,127,0.12); }
.fp-picker-selected { display: flex; align-items: baseline; gap: 8px; font-size: 13px; color: #0A0A0A; }
.fp-picker-id { font-size: 10.5px; color: #9CA3AF; font-weight: 600; }
.fp-picker-placeholder { font-size: 13px; }
.fp-picker-arrow { font-size: 11px; color: #9CA3AF; transition: transform .15s; }
.fp-picker-arrow.open { transform: rotate(180deg); color: #16A37F; }

.fp-picker-panel {
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 10px;
  margin-top: 6px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.10);
  overflow: hidden;
}
.fp-picker-search {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid #F0F2F5;
  background: #FAFAFA;
}
.fp-picker-search input {
  width: 100%;
  padding: 8px 30px 8px 11px;
  border: 1px solid #E5E7EB;
  border-radius: 7px;
  font: 500 12px/1.3 Inter;
  outline: none;
}
.fp-picker-search input:focus { border-color: #16A37F; box-shadow: 0 0 0 3px rgba(22,163,127,0.12); }
.fp-picker-spinner { position: absolute; right: 22px; top: 50%; transform: translateY(-50%); color: #9CA3AF; }

.fp-picker-list {
  list-style: none;
  padding: 4px 0;
  margin: 0;
  max-height: 320px;
  overflow-y: auto;
}
.fp-picker-list li {
  padding: 8px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fp-picker-list li:hover { background: #F0FBF8; }
.fp-picker-list li.chosen { background: #ECFDF5; }
.fp-picker-row-main { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 12.5px; color: #0A0A0A; }
.fp-picker-row-id { font-size: 10.5px; color: #9CA3AF; font-weight: 600; }
.fp-picker-row-sub { font-size: 10.5px; color: #6B7280; }
.fp-picker-empty { padding: 16px 14px; text-align: center; color: #9CA3AF; font-size: 12px; font-style: italic; cursor: default; }
.fp-picker-empty:hover { background: transparent; }

.fp-picker-clear-row { margin-top: 6px; font-size: 11.5px; }
.fp-link { background: none; border: 0; color: #0D7A5F; text-decoration: underline; cursor: pointer; padding: 0; font: inherit; }

.fp-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fp-modal {
  background: #fff;
  border-radius: 14px;
  width: 460px;
  max-width: 92vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.25);
}
.fp-modal-head { flex-shrink: 0; }
.fp-modal-foot { flex-shrink: 0; }
.fp-modal-head {
  padding: 14px 18px;
  border-bottom: 1px solid #F0F2F5;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.fp-modal-close { background: none; border: 0; font-size: 22px; line-height: 1; color: #9CA3AF; cursor: pointer; }
.fp-modal-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 9px; overflow-y: auto; flex: 1; }
.fp-modal-row { font-size: 12px; color: #374151; display: flex; gap: 6px; }
.fp-modal-row span { color: #6B7280; }
.fp-modal-label { display: block; font-size: 10px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: .06em; margin-top: 8px; margin-bottom: 4px; }
.fp-req { color: #F59E0B; font-weight: 700; text-transform: none; letter-spacing: 0; }
.fp-modal-body input,
.fp-modal-body textarea {
  width: 100%;
  padding: 8px 11px;
  border: 1px solid #E5E7EB;
  border-radius: 7px;
  font: 500 13px/1.4 Inter;
  outline: none;
  resize: vertical;
}
.fp-help { font-size: 10.5px; color: #9CA3AF; margin-top: 4px; }
.fp-audit-comment { min-height: 80px; line-height: 1.5; }
.fp-modal-foot {
  padding: 12px 18px;
  background: #FAFAFA;
  border-top: 1px solid #F0F2F5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Ссылка-кнопка «История» */
.fp-btn-link {
  background: none; border: none; padding: 4px 6px;
  color: #0D7A5F; font-size: 12px; font-weight: 600; cursor: pointer;
}
.fp-btn-link:hover { text-decoration: underline; }

/* История депозита (таймлайн) */
.fp-history-empty { color: #6B7280; font-size: 13px; padding: 12px 0; text-align: center; }
.fp-history { list-style: none; margin: 0; padding: 0; }
.fp-history-item {
  padding: 10px 0 12px; border-bottom: 1px solid #F0F2F5;
  border-left: 2px solid #E5E7EB; padding-left: 12px; margin-left: 2px;
}
.fp-history-item:last-child { border-bottom: none; }
.fp-history-top { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.fp-history-type { font-weight: 700; font-size: 13px; color: #111827; }
.fp-history-time { font-size: 11.5px; color: #9CA3AF; white-space: nowrap; }
.fp-history-flow { margin-top: 3px; font-size: 12px; color: #6B7280; }
.fp-history-meta { margin-top: 3px; font-size: 12px; color: #374151; }
.fp-history-note { margin-top: 3px; font-size: 12px; color: #4B5563; font-style: italic; }
.fp-history-actor { margin-top: 3px; font-size: 11.5px; color: #9CA3AF; }
</style>
